import { Outlet, useLoaderData, ScrollRestoration, useLocation } from '@modern-js/runtime/router'

import { useAnalyticsSetup } from '@fe-toolkit/analytics/react'
import { setupEventingBridge, setupOneTrustConsentListener } from '@fe-toolkit/analytics'
import { captureLandingContext } from '@fe-toolkit/utility/client/tracking-context'
import { customEvent } from '@fe-toolkit/eventing'
import tamaguiConfig from '@giffgaff/fe-design-system-ui/config'
import { FederatedComponentBoundary } from '@fe-toolkit/web-components/ssr'

import { useEffect, useState } from 'react'

import CommonHeader from 'fe_common/components/CommonHeader'

import CommonFooter from 'fe_common/components/CommonFooter'

import Providers from '@/components/common/Providers'

import CampaignPromoBar from '@/components/organisms/CampaignPromoBar'

import FallbackHeader from '@/components/fallbacks/FallbackHeader'
import FallbackFooter from '@/components/fallbacks/FallbackFooter'
import { FallbackChromeContext } from '@/components/fallbacks/fallbackChromeFlags'

import { RuntimeConfigContext, serializeConfigForScript, mfeName, basePath } from '@/config/runtimeConfig'
import { BLACK_FRIDAY_TERMS } from '@/config/termsSection'
import { hasTrackingParams } from '@/utils/tracking/campaign'
import { syncGgUtmzConsent } from '@/utils/tracking/ggUtmzConsent'
import { buildCampaignQuery, decorateCampaignLinks } from '@/utils/tracking/outboundCampaign'
import { normalisePathname } from '@/utils/routing/normalisePathname'

import type { RuntimeConfig } from '@/config/runtimeConfig'
import type { ChromeFlags } from '@/api/client-tracking-context/getChromeFlags'
import type { MemberDetails } from '@/api/member/member.types'

/**
 * Iglu schema version the masthead's `cta_interact` events are sent against
 * (FAAS-767).
 *
 * 1-0-2 is the first version declaring `destination`, which the banner-click
 * event carries. giffgaff Iglu schemas are `additionalProperties: false`, so
 * leaving this at the toolkit's 1-0-0 default while sending that field would put
 * every masthead click in Snowplow's bad rows, and bad rows are silent: nothing
 * surfaces in the browser, the data just goes missing in Snowflake.
 *
 * This pin and `trackBannerClick`'s payload move together. See
 * src/utils/tracking/bannerClick.ts.
 */
const CTA_INTERACT_SCHEMA_VERSION = '1-0-2'

/**
 * Renders the legacy campaign promo bar above the header only on `/landing` (FAAS-779).
 *
 * The bar is page content, not chrome, but the frames put it above the federated
 * `CommonHeader`, and this layout is the only place in the tree that sits above it — so it is
 * rendered here and gated on the route rather than from the page component, which can only
 * ever land below the header.
 *
 * Paths are router paths, so they exclude the `basePath` basename that modern.runtime.ts
 * configures — `/landing`, not `/landing-ms/landing`. Trailing slashes are equivalent for
 * routing, so normalise before matching.
 */
const showsCampaignPromo = (pathname: string) => normalisePathname(pathname) === '/landing'

/**
 * The Black Friday terms go in the footer on the homepage only, and only for `black-friday-master`
 * (isBlackFriday, not pre-registration). Gated here because the footer is layout-owned: the page
 * can't reach it. Passed to fe-common's CommonFooter and to the fallback, which hand them to the
 * feds-ui Footer only; the legacy fe-toolkit Footer has no terms section.
 *
 * Off without a server answer, as Black Friday is for the chrome until a lookup returns.
 */
const blackFridayTerms = (pathname: string, flags?: ChromeFlags) =>
  normalisePathname(pathname) === '/' && flags?.isBlackFriday ? BLACK_FRIDAY_TERMS : undefined

export default function Layout() {
  const { memberDetails, runtimeConfig, isBrandRefresh, chromeFlags, localChromeFlags } = useLoaderData() as {
    memberDetails: MemberDetails | null
    runtimeConfig: RuntimeConfig
    isBrandRefresh: boolean
    chromeFlags?: ChromeFlags
    localChromeFlags?: ChromeFlags
  }
  // The chrome's server answer, kept from the server render for the life of the layout: this loader
  // runs again in the browser on every SPA navigation, where it has no context and returns none.
  // fe-common's decision when it was made, otherwise this app's own read of the same flags, which
  // does not need fe-common to be up.
  //
  // CommonHeader and CommonFooter take it too, so both booleans are final there. With
  // isBrandRefresh alone they look Black Friday up in the browser, which can answer differently
  // from the server render and break hydration (React #418): since getChromeFlags was skipped,
  // the server rendered the Header without Black Friday while the browser's first render had it.
  // The footer takes only the two flags it reads: it forwards anything else to the legacy footer.
  const [fallbackChromeFlags] = useState(chromeFlags ?? localChromeFlags)
  const headerFlags = fallbackChromeFlags ?? { isBrandRefresh }
  const footerFlags = fallbackChromeFlags
    ? { isBrandRefresh: fallbackChromeFlags.isBrandRefresh, isBlackFriday: fallbackChromeFlags.isBlackFriday }
    : { isBrandRefresh }

  const { pathname, search } = useLocation()
  const footerTerms = blackFridayTerms(pathname, fallbackChromeFlags)

  // Initialise Snowplow tracker and wire up OneTrust consent on the client.
  // Guard with ENABLE_SNOWPLOW so the tracker can be toggled per environment
  // without a rebuild.
  //
  // anonymousTracking: tracker starts without persistent identifiers so the
  // automatic on-init page view carries no cookies or network_userid. On
  // OnConsentChanged, setup.ts automatically calls disableAnonymousTracking,
  // fires a sessionisation trackPageView, then reports consent.
  useAnalyticsSetup({
    enabled: runtimeConfig.ENABLE_SNOWPLOW === 'true',
    collectorUrl: runtimeConfig.SNOWPLOW_COLLECTOR_URL,
    appId: mfeName || 'web-mfe',
    anonymousTracking: { withServerAnonymisation: true },
    cookieSecure: true,
    basePath,
    pathname,
  })

  // Route @fe-toolkit/eventing customEvent() calls to the Snowplow browser
  // tracker initialised above. Without this, customEvent only reaches Snowplow
  // via the GTM/server-side-GTM data layer, which Google Consent Mode gates on
  // analytics_storage — so events would silently never fire for non-consented
  // users. The tracker runs in anonymous mode pre-consent, so events still fire
  // (without network_userid) before the user consents. No-op until the MFE
  // actually calls customEvent().
  useEffect(() => {
    if (runtimeConfig.ENABLE_SNOWPLOW === 'true') {
      setupEventingBridge({ schemaVersions: { cta_interact: CTA_INTERACT_SCHEMA_VERSION } })
    }
  }, [runtimeConfig.ENABLE_SNOWPLOW])

  // Keep the __ggUtmz Portal bridge in step with consent (FAAS-782).
  //
  // The SSR middleware only sees consent as it stood when the document was
  // requested. A visitor arriving from a campaign with no consent yet, accepting
  // the banner, then walking into a Portal journey never triggers another
  // document request, so without this the cookie is never written and the order
  // loses its attribution. Withdrawal has to expire it for the same reason in
  // reverse: Portal reads the cookie's absence as the consent signal.
  //
  // Both callbacks do the same thing because the sync reads current consent
  // itself; `onAlreadyConsented` covers returning visitors, for whom OneTrust
  // never fires OnConsentChanged.
  useEffect(() => {
    const sync = () => syncGgUtmzConsent(runtimeConfig.COOKIE_DOMAIN)

    return setupOneTrustConsentListener(sync, sync)
  }, [runtimeConfig.COOKIE_DOMAIN])

  // Carry campaign parameters onto every outbound Portal link (FAAS-750).
  //
  // The __ggUtmz bridge above is not enough on its own, because Portal only reads
  // that cookie while its session campaign is still empty. Accepting the OneTrust
  // banner on any parameter-less Portal page makes Portal's own writer set the
  // cookie to the literal `(direct)`, the next Portal request latches that into
  // the session, and a correct cookie written later is never read again. A URL
  // parameter has no such guard: Portal's ggCampaign and utm_campaign branches
  // call App::session('campaign', true), which unsets the namespace first, so a
  // parameter overwrites whatever is latched. See src/utils/tracking/outboundCampaign.ts.
  //
  // Reads the landing snapshot rather than the current URL, so the campaign still
  // travels after several in-app navigations have cleaned the address bar — the
  // same reason syncGgUtmzConsent reads it.
  //
  // A MutationObserver rather than a one-shot pass because CommonHeader/CommonFooter are
  // federated remotes that hydrate on their own schedule, and they are exactly the Portal
  // links a visitor is most likely to click.
  // decorateCampaignLinks is idempotent, and the observer watches childList only,
  // so rewriting an href cannot retrigger it.
  //
  // Deps are `[]`, not `[pathname]`: nothing in this effect reads `pathname` — campaignQuery
  // comes from the landing snapshot, not the current URL — and the observer already watches
  // `document.body` with `subtree: true`, so an in-app navigation's own DOM swap under
  // `<Outlet>` is itself a mutation the existing observer picks up. Keying this on `pathname`
  // only tore the observer down and reran a full-page decorate() on every navigation for no
  // behavioural gain.
  useEffect(() => {
    const campaignQuery = buildCampaignQuery(captureLandingContext()?.search ?? '')

    if (!campaignQuery) {
      return
    }

    const { origin } = window.location
    let queued = false

    const decorate = () => {
      queued = false
      decorateCampaignLinks(campaignQuery, document, origin)
    }

    decorate()

    // Coalesce bursts of DOM changes into one pass per frame. Without this a
    // chatty render would run querySelectorAll on every individual mutation.
    const observer = new MutationObserver(() => {
      if (queued) {
        return
      }

      queued = true
      window.requestAnimationFrame(decorate)
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  // Campaign-linking event, matching the pattern in fe-devices' layout and
  // fe-phones' _app (FAAS-765). Keyed on `search` rather than on a derived
  // boolean so a second tracked landing within the same session fires again —
  // a boolean dependency stays `true` and silently swallows the repeat.
  //
  // Declared after the bridge effect above deliberately: effects run in
  // declaration order, so the Snowplow adapter is registered before this fires
  // and the event reaches the collector as well as the dataLayer.
  useEffect(() => {
    if (hasTrackingParams(search)) {
      customEvent('initiate_tracking', {})
    }
  }, [search])

  // Serialize runtime config as an inline script so it's available to non-React
  // code (API client, MSW init in modern.runtime.ts, etc.) BEFORE the main
  // bundle's module-init code runs. The useEffect approach was too late —
  // modern.runtime.ts executes at import time, before React hydration.
  //
  // serializeConfigForScript escapes `<`, `\u2028`, and `\u2029` to prevent
  // script-tag breakout and XSS via crafted config values.
  //
  // CSP note: This inline <script> requires either 'unsafe-inline' or a
  // nonce (e.g. `script-src 'nonce-<random>'`) in the Content-Security-Policy
  // header. If a strict CSP is introduced, generate a per-request nonce in
  // the SSR response and attach it via `<script nonce={nonce}>`. Modern.js
  // custom server middleware can inject the nonce into both the CSP header
  // and the React render context.
  //
  // The Portal parity dataLayer is NOT pushed here. It has to land in <head>
  // ahead of GTM's bootstrap, which a body script cannot do — see
  // server/dataLayerInjection.ts.
  const runtimeConfigScript = `window.__RUNTIME_CONFIG__=${serializeConfigForScript(runtimeConfig)};`

  return (
    <>
      <RuntimeConfigContext.Provider value={runtimeConfig}>
        <script dangerouslySetInnerHTML={{ __html: runtimeConfigScript }} />
        {/*
          Tamagui base stylesheet for SSR. @fe-toolkit/rsbuild-plugin-tamagui
          AOT-extracts the per-usage layout atomic CSS into linked stylesheets,
          but the styled-component base CSS (Button variants, theme tokens, pseudo
          states) lives in the Tamagui config and is injected at runtime by
          TamaguiProvider on the client. For SSR we emit it here via getCSS() so
          components are styled on first paint. Components register their styles at
          import time, so getCSS() reflects everything loaded so far in the server
          process. NOTE: routes are code-split, so the very first request to a
          freshly-started server can run getCSS() before that route's components
          have registered (brief FOUC); every subsequent request is fully styled,
          and k8s readiness probes warm the process before real traffic.
          getCSS() returns different content on the server vs the client (the
          client also injects at runtime via TamaguiProvider), so
          suppressHydrationWarning is required to avoid a dangerouslySetInnerHTML
          hydration mismatch on this element. See docs/tamagui-integration.md.
        */}
        <style
          dangerouslySetInnerHTML={{ __html: tamaguiConfig.getCSS() }}
          suppressHydrationWarning
          id="tamagui-base"
        />
        <Providers memberId={memberDetails?.id} runtimeConfig={runtimeConfig}>
          <ScrollRestoration />
          <FallbackChromeContext.Provider value={fallbackChromeFlags}>
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              {showsCampaignPromo(pathname) && <CampaignPromoBar />}
              {/*
              fe-common owns the header/footer variant. chromeFlags is its own decision for both
              flags, made in the server loader, so the SSR HTML is final and nothing switches
              after load. Without it, only isBrandRefresh is passed: CommonHeader and CommonFooter
              then resolve their Eppo flags through the shared CTC provider above, render
              isBrandRefresh until that lookup returns, and count Black Friday as off until then.
            */}
              <FederatedComponentBoundary
                fallback={<FallbackHeader username={memberDetails?.memberName} />}
                logLabel="fe_common Header"
              >
                <CommonHeader {...headerFlags} username={memberDetails?.memberName} />
              </FederatedComponentBoundary>
              <main aria-labelledby="main-title" id="main-content" style={{ flex: 1 }}>
                <Outlet />
              </main>
              <FederatedComponentBoundary fallback={<FallbackFooter terms={footerTerms} />} logLabel="fe_common Footer">
                <CommonFooter {...footerFlags} terms={footerTerms} />
              </FederatedComponentBoundary>
            </div>
          </FallbackChromeContext.Provider>
        </Providers>
      </RuntimeConfigContext.Provider>
    </>
  )
}
